import { Element } from '@src/editor/models/Element';
import { VideoElement } from '@src/editor/models/elements/VideoElement';
import { LogManager } from '@src/utility/Logger';

import type { ClipSpan } from './DubPlacementService';
import type { Timeline } from '@src/editor/models/Timeline';

const logger = LogManager.getLogger('editor:readOriginalVideo');

/**
 * The clip's original video, read off the timeline.
 *
 * `elementIds` is every video element the clip plays the original recording
 * through — the set whose level is dropped so the dub is what is heard. A
 * composition's inner video elements count too, which is why this walks into
 * one.
 *
 * `spans` is the clip's kept stretches of the source recording, which tells the
 * placement where each dubbed line lands. Only top-level video elements
 * contribute one: an element nested in a composition carries a time measured
 * against that composition rather than against the clip, so it is reported and
 * skipped rather than guessed at — a wrong span slides the dub against the
 * picture with no error.
 */
export function readOriginalVideo(
  timeline: Pick<Timeline, 'allElements'>,
  isOriginalVideoElement: (probe: unknown) => boolean,
): { spans: ClipSpan[]; elementIds: string[] } {
  const spans: ClipSpan[] = [];
  const elementIds: string[] = [];

  for (const element of timeline.allElements) {
    const inner = Element.isCompositionElement(element)
      ? element.elements.filter(
          (child): child is VideoElement => child instanceof VideoElement,
        )
      : [];
    const probe = Element.isCompositionElement(element)
      ? {
          type: element.type,
          elements: element.elements.map(child => ({
            type: child.type,
            source: child instanceof VideoElement ? child.source : undefined,
          })),
        }
      : {
          type: element.type,
          source: element instanceof VideoElement ? element.source : undefined,
        };

    if (!isOriginalVideoElement(probe)) continue;

    if (element instanceof VideoElement) {
      elementIds.push(element.id);
      spans.push({
        sourceStartSec: element.getMediaStartTime(),
        durationSec: element.durationSeconds,
        clipStartSec: element.time,
      });
      continue;
    }

    for (const child of inner) {
      elementIds.push(child.id);
    }
    if (inner.length > 0) {
      logger.warn(
        'The clip plays its original video inside a composition; its span is not derivable here',
        { elementId: element.id, innerVideoCount: inner.length },
      );
    }
  }

  spans.sort((a, b) => a.clipStartSec - b.clipStartSec);
  return { spans, elementIds };
}
